import React, { useState } from 'react';
import { useQuery } from 'react-query';
import { Typography, Card, Table, Tag, Space, DatePicker, Button, Input } from 'antd';
import { DownloadOutlined } from '@ant-design/icons';
import { apiClient } from '../services/apiClient';

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;

const AuditPage = () => {
  const [filters, setFilters] = useState({
    dateRange: null,
    email: '',
  });

  // Fetch audit logs
  const { data: auditLogs, isLoading } = useQuery(
    ['audit-logs', filters],
    async () => {
      const params = new URLSearchParams();
      if (filters.dateRange && filters.dateRange.length === 2) {
        params.append('startDate', filters.dateRange[0].format('YYYY-MM-DD'));
        params.append('endDate', filters.dateRange[1].format('YYYY-MM-DD'));
      }
      if (filters.email) {
        params.append('email', filters.email);
      }

      const response = await apiClient.get(`/api/audit?${params.toString()}`);
      return response.data.data;
    }
  );

  const columns = [
    {
      title: 'User Email',
      dataIndex: ['user', 'email'],
      key: 'userEmail',
      render: (email) => <Text strong>{email}</Text>,
    },
    {
      title: 'Role',
      dataIndex: ['user', 'role'],
      key: 'userRole',
      render: (role) => <Tag color="blue">{role}</Tag>,
    },
    {
      title: 'Action',
      dataIndex: 'action',
      key: 'action',
      render: (action) => {
        const colors = {
          CREATE: 'green',
          UPDATE: 'blue',
          DELETE: 'red',
          APPROVE: 'purple',
          REJECT: 'orange',
        };
        return <Tag color={colors[action] || 'default'}>{action}</Tag>;
      },
    },
    {
      title: 'Description',
      dataIndex: 'description',
      key: 'description',
      ellipsis: true,
    },
    {
      title: 'Page',
      dataIndex: 'page',
      key: 'page',
      render: (page) => <Tag color="geekblue">{page}</Tag>,
    },
    {
      title: 'Timestamp',
      dataIndex: 'createdAt',
      key: 'timestamp',
      render: (date) => new Date(date).toLocaleString(),
      sorter: (a, b) => new Date(a.createdAt) - new Date(b.createdAt),
    },
  ];

  return (
    <div>
      <div style={{ marginBottom: '24px' }}>
        <Title level={2}>Audit Trail</Title>
        <Text type="secondary">
          Track all system activities and changes
        </Text>
      </div>

      <Card>
        <div style={{ marginBottom: '16px' }}>
          <Space wrap>
            <RangePicker
              placeholder={['Start Date', 'End Date']}
              value={filters.dateRange}
              onChange={(dates) => setFilters(prev => ({ ...prev, dateRange: dates }))}
            />
            <Input.Search
              placeholder="Filter by email"
              style={{ width: 200 }}
              value={filters.email}
              onChange={(e) => setFilters(prev => ({ ...prev, email: e.target.value }))}
              allowClear
            />
            <Button icon={<DownloadOutlined />}>Export</Button>
          </Space>
        </div>

        <Table
          columns={columns}
          dataSource={auditLogs}
          loading={isLoading}
          rowKey="id"
          pagination={{
            pageSize: 20,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) =>
              `${range[0]}-${range[1]} of ${total} audit entries`,
          }}
        />
      </Card>
    </div>
  );
};

export default AuditPage;
